import React, { useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Alert,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  alpha,
  useTheme,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Helmet } from 'react-helmet';
import proxy from '../../api/axios';
import { useDispatch } from 'react-redux';
import { showAuth } from '../../redux/authSlice';
import BrandMark from '../BrandMark';

const ACCENT = '#137274';

const ResetPassword = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { token } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.14)';
  const surface = isDark ? '#121a1c' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const pageBg = isDark ? '#0f1419' : '#f4f8f9';

  const fieldSx = {
    mb: 1.75,
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#0f2a2b', 0.02),
      '& fieldset': { borderColor: border },
      '&:hover fieldset': { borderColor: alpha(ACCENT, 0.55) },
      '&.Mui-focused fieldset': { borderColor: ACCENT, borderWidth: 1.5 },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError('');
    setSuccess('');

    if (!password || !confirmPassword) {
      setError('Enter and confirm your new password.');
      return;
    }
    if (password.length < 8) {
      setError('Use at least 8 characters for your password.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const response = await proxy.post('/auth/reset-password', { token, password });
      if (response.data.success) {
        setSuccess('Password updated. You can sign in with your new password.');
        setTimeout(() => {
          navigate('/');
          dispatch(showAuth());
        }, 1800);
      } else {
        setError(response.data.message || 'Could not reset password.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Error resetting password. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 64px)',
        bgcolor: pageBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 6,
        backgroundImage: isDark
          ? `radial-gradient(ellipse 70% 50% at 50% 0%, ${alpha(ACCENT, 0.22)}, transparent 60%)`
          : `radial-gradient(ellipse 70% 50% at 50% 0%, ${alpha(ACCENT, 0.12)}, transparent 60%)`,
      }}
    >
      <Helmet>
        <title>Reset password | CrafterHack</title>
      </Helmet>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: '100%',
          maxWidth: 420,
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          p: { xs: 2.75, sm: 3.5 },
          boxShadow: isDark
            ? '0 24px 64px rgba(0,0,0,0.45)'
            : '0 24px 64px rgba(15,60,70,0.12)',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2.75 }}>
          <BrandMark />
          <Box
            sx={{
              mt: 2,
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.12),
              color: ACCENT,
            }}
          >
            <LockOutlinedIcon />
          </Box>
          <Typography
            sx={{
              mt: 1.75,
              fontWeight: 900,
              fontSize: '1.35rem',
              letterSpacing: '-0.03em',
              textAlign: 'center',
            }}
          >
            Choose a new password
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              color: muted,
              fontSize: '0.88rem',
              textAlign: 'center',
              lineHeight: 1.5,
              maxWidth: 300,
            }}
          >
            Make it strong — at least 8 characters. You’ll use this to sign in next time.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontSize: '0.82rem' }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2, fontSize: '0.82rem' }}>
            {success}
          </Alert>
        )}

        <TextField
          label="New password"
          type={showPassword ? 'text' : 'password'}
          fullWidth
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          sx={fieldSx}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Toggle password visibility"
                  onClick={() => setShowPassword((v) => !v)}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Confirm password"
          type={showConfirm ? 'text' : 'password'}
          fullWidth
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          sx={fieldSx}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Toggle confirm password visibility"
                  onClick={() => setShowConfirm((v) => !v)}
                  edge="end"
                  size="small"
                >
                  {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading || !!success}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            py: 1.25,
            borderRadius: 2,
            bgcolor: ACCENT,
            boxShadow: 'none',
            mt: 0.5,
            '&:hover': { bgcolor: '#0f5c5e', boxShadow: 'none' },
          }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : 'Update password'}
        </Button>

        <Typography
          sx={{
            mt: 2.5,
            textAlign: 'center',
            fontSize: '0.85rem',
            color: muted,
          }}
        >
          Remembered it?{' '}
          <Box
            component={RouterLink}
            to="/"
            onClick={() => dispatch(showAuth())}
            sx={{
              color: ACCENT,
              fontWeight: 800,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Sign in
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

export default ResetPassword;
